import { firestoreService } from '../firestoreService.js';
import { COLLECTIONS } from '../utils/constants.js';
import { TASKS_ENABLED } from '../config/featureFlags.js';

/**
 * MenuService - Centralized service for menu operations
 * Handles all CRUD operations, validation, and business logic for menu items
 */
class MenuService {
  constructor() {
    this.collectionName = COLLECTIONS.MENU_ITEMS;
    this.isInitializing = false;
    this.initializationPromise = null;
    this._cache = null;
    this._cacheTimestamp = null;
    this.CACHE_TTL = 30000; // 30 seconds cache TTL
  }

  /**
   * Check if cache is valid
   */
  _isCacheValid() {
    return this._cache && 
           this._cacheTimestamp && 
           (Date.now() - this._cacheTimestamp) < this.CACHE_TTL;
  }

  /**
   * Clear cache
   */
  clearCache() {
    this._cache = null;
    this._cacheTimestamp = null;
    console.log('[MenuService] Cache cleared');
  }

  /**
   * Get all menu items from Firestore with caching
   * Menu items are GLOBAL - not school-specific
   * @param {string} schoolId - IGNORED - kept for backward compatibility only
   * @param {boolean} forceRefresh - Force refresh bypassing cache
   */
  async getAllMenuItems(schoolId, forceRefresh = false) {
    try {
      // Return cached data if valid and not forcing refresh
      if (!forceRefresh && this._isCacheValid()) {
        console.log('[MenuService] Returning cached menu items');
        return this._cache;
      }

      console.log('[MenuService] Fetching fresh menu items from Firestore (global, not school-specific)');
      // Menu items are GLOBAL configuration - same for all schools
      const items = await firestoreService.getAll(this.collectionName);
      const cleanItems = this.removeDuplicates(items);

      // Update cache
      this._cache = cleanItems;
      this._cacheTimestamp = Date.now();

      return cleanItems;
    } catch (error) {
      console.error('[MenuService] Error loading menu items:', error);
      throw new Error(`Failed to load menu items: ${error.message}`);
    }
  }

  /**
   * Create a new menu item
   * @param {Object} menuItem - Menu item data (global, not school-specific)
   */
  async createMenuItem(menuItem) {
    try {
      this.validateMenuItem(menuItem);
      const { id, schoolId, ...itemData } = menuItem; // Remove schoolId if present

      // Ensure parentId is explicitly set to null if not provided
      const dataWithDefaults = {
        ...itemData,
        parentId: itemData.parentId ?? null
      };

      const result = await firestoreService.setWithId(this.collectionName, id, dataWithDefaults);

      // Clear cache after creating
      this.clearCache();

      return result;
    } catch (error) {
      console.error('[MenuService] Error creating menu item:', error);
      throw new Error(`Failed to create menu item: ${error.message}`);
    }
  }

  /**
   * Update an existing menu item
   */
  async updateMenuItem(id, updates) {
    try {
      console.log('[MenuService] Updating menu item:', id, 'with updates:', updates);

      // Clean the updates object - remove any undefined values and Firestore-specific fields
      const cleanUpdates = {};
      for (const [key, value] of Object.entries(updates)) {
        if (value !== undefined &&
            key !== 'id' &&
            key !== 'createdAt' &&
            key !== 'updatedAt') {
          cleanUpdates[key] = value;
        }
      }

      console.log('[MenuService] Clean updates:', cleanUpdates);

      // Use setWithId with merge to ensure proper updating
      const result = await firestoreService.setWithId(this.collectionName, id, cleanUpdates, { merge: true });

      // Clear cache after updating
      this.clearCache();

      console.log('[MenuService] Successfully updated menu item:', id);
      return result;
    } catch (error) {
      console.error('[MenuService] Error updating menu item:', error);
      console.error('[MenuService] Full error object:', error);
      throw error; // Don't wrap the error to preserve the original Firebase error details
    }
  }

  /**
   * Delete a menu item
   */
  async deleteMenuItem(id) {
    try {
      const result = await firestoreService.delete(this.collectionName, id);
      
      // Clear cache after deleting
      this.clearCache();
      
      return result;
    } catch (error) {
      console.error('[MenuService] Error deleting menu item:', error);
      throw new Error(`Failed to delete menu item: ${error.message}`);
    }
  }

  /**
   * Bulk update multiple menu items
   */
  async bulkUpdateMenuItems(menuItems) {
    try {
      
      const updatePromises = menuItems.map(item => {
        const { id, children: _children, ...itemData } = item; // Strip children field
        
        // Ensure all required fields are present before update
        this.validateMenuItem({ id, ...itemData });
        return firestoreService.setWithId(this.collectionName, id, itemData);
      });

      await Promise.all(updatePromises);
      
      // Clear cache after bulk update
      this.clearCache();
      
    } catch (error) {
      console.error('[MenuService] Error in bulk update:', error);
      console.error('[MenuService] Failed items:', menuItems.map(item => ({ id: item.id, hasChildren: !!item.children })));
      throw new Error(`Failed to bulk update menu items: ${error.message}`);
    }
  }

  /**
   * Initialize default menu items if none exist
   */
  async initializeDefaultMenuItems(defaultMenuItems, schoolId) {
    // CRITICAL: Never initialize without schoolId - would violate security rules
    if (!schoolId) {
      console.error('[MenuService] Cannot initialize menu items without schoolId');
      return;
    }

    // Prevent multiple simultaneous initialization attempts
    if (this.isInitializing) {
      return this.initializationPromise;
    }

    this.isInitializing = true;

    this.initializationPromise = (async () => {
      try {
        // Add schoolId to each menu item before creating
        const itemsWithSchool = defaultMenuItems.map(item => ({
          ...item,
          schoolId
        }));

        const results = await Promise.allSettled(
          itemsWithSchool.map(item => this.createMenuItem(item))
        );

        const failed = results.filter(r => r.status === 'rejected');

        if (failed.length > 0) {
          console.error('[MenuService] Some items failed to initialize:', failed.map(f => f.reason));
          throw new Error(`Failed to initialize ${failed.length} menu items`);
        }

      } catch (error) {
        console.error('[MenuService] Error initializing default menu items:', error);
        throw error;
      } finally {
        this.isInitializing = false;
        this.initializationPromise = null;
      }
    })();

    return this.initializationPromise;
  }

  /**
   * Filter menu items based on role and visibility
   */
  filterMenuItemsByRole(menuItems, role) {
    if (!role) return [];

    return menuItems.filter(item => {
      if (!TASKS_ENABLED && item.id === 'my-tasks') return false;

      // Check if item is visible
      if (!item.visible) return false;

      // Check if current role has access to this item
      return item.roles && item.roles[role];
    });
  }

  /**
   * Filter hierarchical menu items based on role with cascading behavior
   * If a parent is filtered out, all its children are automatically removed
   * @param {Array} hierarchyItems - Built hierarchy (array of root items with children)
   * @param {string} role - Current user role
   * @returns {Array} Filtered hierarchy maintaining nested structure
   */
  filterHierarchyByRole(hierarchyItems, role) {
    if (!role) return [];

    return hierarchyItems
      .filter(item => {
        if (!TASKS_ENABLED && item.id === 'my-tasks') return false;
        if (!item.visible) return false;
        if (item.showInMenu === false) return false;
        return item.roles && item.roles[role];
      })
      .map(item => {
        // If item has children, recursively filter them
        if (item.children && item.children.length > 0) {
          return {
            ...item,
            children: this.filterHierarchyByRole(item.children, role)
          };
        }
        return item;
      });
  }

  /**
   * Remove duplicate menu items by ID
   */
  removeDuplicates(menuItems) {
    const uniqueItems = menuItems.reduce((acc, item) => {
      if (!acc.find(existing => existing.id === item.id)) {
        acc.push(item);
      } else {
        // Item already exists, skip
      }
      return acc;
    }, []);
    
    return uniqueItems.sort((a, b) => a.order - b.order);
  }

  /**
   * Validate menu item structure
   */
  validateMenuItem(menuItem) {
    
    if (!menuItem.id) {
      throw new Error('Menu item must have an ID');
    }
    
    if (!menuItem.label) {
      throw new Error('Menu item must have a label');
    }
    
    // href is optional — parent/dropdown container items don't have one
    
    if (!menuItem.roles || typeof menuItem.roles !== 'object') {
      throw new Error('Menu item must have roles object');
    }
    
    if (typeof menuItem.order !== 'number') {
      throw new Error('Menu item must have a numeric order');
    }
    
    if (typeof menuItem.visible !== 'boolean') {
      throw new Error('Menu item must have a boolean visible property');
    }

    // Validate inDevelopment flag (optional)
    if (menuItem.inDevelopment !== undefined && typeof menuItem.inDevelopment !== 'boolean') {
      throw new Error('Menu item inDevelopment must be a boolean');
    }

    // Validate hierarchical properties (optional)
    if (menuItem.parentId !== undefined && menuItem.parentId !== null && typeof menuItem.parentId !== 'string') {
      throw new Error('Menu item parentId must be a string or null');
    }

    if (menuItem.depth !== undefined && typeof menuItem.depth !== 'number') {
      throw new Error('Menu item depth must be a number');
    }

    // Skip children validation - it's used for hierarchical display only
  }

  /**
   * Generate a unique ID for a new menu item
   */
  generateMenuItemId(label) {
    const timestamp = Date.now();
    const cleanLabel = label.toLowerCase().replace(/[^a-z0-9]/g, '');
    return `${cleanLabel}_${timestamp}`;
  }

  /**
   * Reorder menu items
   */
  reorderMenuItems(menuItems, fromIndex, toIndex) {
    const reorderedItems = [...menuItems];
    const [movedItem] = reorderedItems.splice(fromIndex, 1);
    reorderedItems.splice(toIndex, 0, movedItem);
    
    // Update order values
    return reorderedItems.map((item, index) => ({
      ...item,
      order: index + 1
    }));
  }

  /**
   * Build hierarchical structure from flat menu items
   */
  buildHierarchy(menuItems) {
    const itemMap = new Map();
    const rootItems = [];

    // Create a map of all items
    menuItems.forEach(item => {
      itemMap.set(item.id, { ...item, children: [] });
    });

    // Build the hierarchy
    menuItems.forEach(item => {
      const menuItem = itemMap.get(item.id);
      if (item.parentId && itemMap.has(item.parentId)) {
        const parent = itemMap.get(item.parentId);
        parent.children.push(menuItem);
      } else {
        rootItems.push(menuItem);
      }
    });

    return rootItems;
  }

  /**
   * Flatten hierarchical menu structure
   */
  flattenHierarchy(hierarchicalItems, depth = 0, parentId = null) {
    const flatItems = [];

    hierarchicalItems.forEach((item, index) => {
      const flatItem = {
        ...item,
        depth,
        parentId,
        order: index + 1,
        children: undefined // Remove children from flat structure
      };
      flatItems.push(flatItem);

      if (item.children && item.children.length > 0) {
        const childItems = this.flattenHierarchy(item.children, depth + 1, item.id);
        flatItems.push(...childItems);
      }
    });

    return flatItems;
  }

  /**
   * Move item to make it a child of another item
   */
  moveItemToParent(menuItems, itemId, newParentId) {
    const item = menuItems.find(i => i.id === itemId);
    if (!item) return menuItems;

    // Prevent making an item a child of its own descendant
    if (this.isDescendant(menuItems, newParentId, itemId)) {
      console.warn('Cannot make item a child of its own descendant');
      return menuItems;
    }

    return menuItems.map(i => {
      if (i.id === itemId) {
        return {
          ...i,
          parentId: newParentId,
          depth: newParentId ? this.getItemDepth(menuItems, newParentId) + 1 : 0
        };
      }
      return i;
    });
  }

  /**
   * Check if targetId is a descendant of ancestorId
   */
  isDescendant(menuItems, targetId, ancestorId) {
    const target = menuItems.find(i => i.id === targetId);
    if (!target || !target.parentId) return false;
    
    if (target.parentId === ancestorId) return true;
    
    return this.isDescendant(menuItems, target.parentId, ancestorId);
  }

  /**
   * Get the depth of an item
   */
  getItemDepth(menuItems, itemId) {
    const item = menuItems.find(i => i.id === itemId);
    if (!item || !item.parentId) return 0;
    
    return this.getItemDepth(menuItems, item.parentId) + 1;
  }

  /**
   * Update depths for all items after hierarchy changes
   */
  updateDepths(menuItems) {
    return menuItems.map(item => ({
      ...item,
      depth: this.getItemDepth(menuItems, item.id)
    }));
  }
}

// Export singleton instance
export const menuService = new MenuService();
export default menuService;
